import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, switchMap } from 'rxjs';
import { AuthService } from './Auth/auth.service';
import { environment } from '../../environments/environment';
/* istanbul ignore next */
@Injectable({
  providedIn: 'root',
})
export class AddressServiceService {
  private readonly ipifyUrl = 'https://api64.ipify.org?format=json';
  constructor(
    private readonly http: HttpClient,
    private readonly userAuth: AuthService
  ) {}
  /* istanbul ignore next */
  usStates = [
    { name: 'Alabama', abbreviation: 'AL' },
    { name: 'Alaska', abbreviation: 'AK' },
    { name: 'Arizona', abbreviation: 'AZ' },
    { name: 'Arkansas', abbreviation: 'AR' },
    { name: 'California', abbreviation: 'CA' },
    { name: 'Colorado', abbreviation: 'CO' },
    { name: 'Connecticut', abbreviation: 'CT' },
    { name: 'Delaware', abbreviation: 'DE' },
    { name: 'Florida', abbreviation: 'FL' },
    { name: 'Georgia', abbreviation: 'GA' },
    { name: 'Hawaii', abbreviation: 'HI' },
    { name: 'Idaho', abbreviation: 'ID' },
    { name: 'Illinois', abbreviation: 'IL' },
    { name: 'Indiana', abbreviation: 'IN' },
    { name: 'Iowa', abbreviation: 'IA' },
    { name: 'Kansas', abbreviation: 'KS' },
    { name: 'Kentucky', abbreviation: 'KY' },
    { name: 'Louisiana', abbreviation: 'LA' },
    { name: 'Maine', abbreviation: 'ME' },
    { name: 'Maryland', abbreviation: 'MD' },
    { name: 'Massachusetts', abbreviation: 'MA' },
    { name: 'Michigan', abbreviation: 'MI' },
    { name: 'Minnesota', abbreviation: 'MN' },
    { name: 'Mississippi', abbreviation: 'MS' },
    { name: 'Missouri', abbreviation: 'MO' },
    { name: 'Montana', abbreviation: 'MT' },
    { name: 'Nebraska', abbreviation: 'NE' },
    { name: 'Nevada', abbreviation: 'NV' },
    { name: 'New Hampshire', abbreviation: 'NH' },
    { name: 'New Jersey', abbreviation: 'NJ' },
    { name: 'New Mexico', abbreviation: 'NM' },
    { name: 'New York', abbreviation: 'NY' },
    { name: 'North Carolina', abbreviation: 'NC' },
    { name: 'North Dakota', abbreviation: 'ND' },
    { name: 'Ohio', abbreviation: 'OH' },
    { name: 'Oklahoma', abbreviation: 'OK' },
    { name: 'Oregon', abbreviation: 'OR' },
    { name: 'Pennsylvania', abbreviation: 'PA' },
    { name: 'Rhode Island', abbreviation: 'RI' },
    { name: 'South Carolina', abbreviation: 'SC' },
    { name: 'South Dakota', abbreviation: 'SD' },
    { name: 'Tennessee', abbreviation: 'TN' },
    { name: 'Texas', abbreviation: 'TX' },
    { name: 'Utah', abbreviation: 'UT' },
    { name: 'Vermont', abbreviation: 'VT' },
    { name: 'Virginia', abbreviation: 'VA' },
    { name: 'Washington', abbreviation: 'WA' },
    { name: 'West Virginia', abbreviation: 'WV' },
    { name: 'Wisconsin', abbreviation: 'WI' },
    { name: 'Wyoming', abbreviation: 'WY' },
  ];

  // List of Canadian provinces
  canadaProvinces = [
    { name: 'Alberta', abbreviation: 'AB' },
    { name: 'British Columbia', abbreviation: 'BC' },
    { name: 'Manitoba', abbreviation: 'MB' },
    { name: 'New Brunswick', abbreviation: 'NB' },
    { name: 'Newfoundland and Labrador', abbreviation: 'NL' },
    { name: 'Nova Scotia', abbreviation: 'NS' },
    { name: 'Ontario', abbreviation: 'ON' },
    { name: 'Prince Edward Island', abbreviation: 'PE' },
    { name: 'Quebec', abbreviation: 'QC' },
    { name: 'Saskatchewan', abbreviation: 'SK' },
    { name: 'Northwest Territories', abbreviation: 'NT' },
    { name: 'Nunavut', abbreviation: 'NU' },
    { name: 'Yukon', abbreviation: 'YT' },
  ];
  /* istanbul ignore next */
  getUSStates() {
    return this.usStates;
  }
  /* istanbul ignore next */
  getCanadaProvinces() {
    return this.canadaProvinces;
  }

  /* istanbul ignore next */
  validateAddress(address: string): Observable<any> {
    const apiUrl =
      environment.addressValidationEnvironmentURL +
      `/address-validation/search`;

    return this.userAuth.getTokenSilently$().pipe(
      switchMap((token) => {
        const options = {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          }),
          params: {
            query: address,
            limit: '20',
            typeahead: 'true',
            language: 'en-US',
            countrySet: 'US',
          },
        };

        return this.http.get<any>(apiUrl, options);
      })
    );
  }

  /* istanbul ignore next */
  getIpAddress(): Observable<{ ip: string }> {
    return this.http.get<{ ip: string }>(this.ipifyUrl);
  }

  /* istanbul ignore next */
  filterValidAddresses(results: any[]): any[] {
    return results.filter(
      (suggestion: any) =>
        suggestion.type === 'Point Address' ||
        suggestion.type === 'Address Range'
    );
  }

  /* istanbul ignore next */
  isValidStreetAddress(address: string): boolean {
    // Check if the address contains both a number and a word
    const hasNumber = /\d/.test(address);
    const hasWord = /\b[A-Za-z]+\b/.test(address);
    return hasNumber && hasWord;
  }

  /* istanbul ignore next */
  fetchFilteredAddressSuggestions(address: string): Observable<any[]> {
    return new Observable((observer) => {
      this.validateAddress(address).subscribe({
        next: (response) => {
          const filteredSuggestions = this.filterValidAddresses(
            response.results
          );
          observer.next(filteredSuggestions);
          observer.complete();
        },
        error: (error) => {
          console.error('Failed to fetch address suggestions:', error);
          observer.error(error);
        },
      });
    });
  }
}
